import { useEffect } from 'react';
import { dirOf, type Locale } from '@castfeeds/i18n';
import { api } from '@/lib/api/endpoints';
import { logError } from '@/lib/log';
import { LOCALE_CHOSEN_KEY } from '@/lib/branding';
import { resolveTheme, usePrefs } from '@/store/prefs';
import { useSession } from '@/store/session';
import type { AuthSession, ThemePref } from '@/lib/api/types';

/** Keep <html lang/dir/data-theme> in step with the preferences (and the OS scheme when theme = system). */
export function usePrefsEffects(): void {
  const locale = usePrefs((s) => s.locale);
  const theme = usePrefs((s) => s.theme);
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = dirOf(locale);
  }, [locale]);
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: light)');
    const apply = () => {
      document.documentElement.dataset.theme = resolveTheme(theme, !!mq?.matches);
    };
    apply();
    if (theme !== 'system' || !mq) return;
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [theme]);
}

/** Save a preference locally and, when signed in, to the profile (PATCH /v1/auth/me). */
async function syncToProfile(body: { locale?: Locale; theme?: ThemePref }): Promise<void> {
  if (!useSession.getState().token) return;
  try {
    const profile = await api.auth.patchMe(body);
    useSession.getState().setProfile(profile);
  } catch (e) {
    logError('prefs', `could not save ${Object.keys(body).join(',')} to the profile; kept locally`, e);
  }
}

export function setLocalePref(locale: Locale): Promise<void> {
  usePrefs.getState().setLocale(locale);
  try {
    localStorage.setItem(LOCALE_CHOSEN_KEY, '1');
  } catch {
    // Private mode: the choice still holds for this visit.
  }
  return syncToProfile({ locale });
}

export function setThemePref(theme: ThemePref): Promise<void> {
  usePrefs.getState().setTheme(theme);
  return syncToProfile({ theme });
}

/** After login/register: store the session and adopt the profile's language and theme. */
export function adoptSession(s: AuthSession): void {
  useSession.getState().setSession(s);
  usePrefs.getState().setLocale(s.user.locale === 'he' ? 'he' : 'en');
  usePrefs.getState().setTheme(s.user.theme ?? 'system');
}
